import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, Calendar, GitCompare, TrendingUp, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import EventDialog from '@/components/EventDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  not_applied: 'bg-zinc-700 text-zinc-300',
  applied: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  sold_out: 'bg-slate-500/20 text-slate-400',
  attending_partner: 'bg-purple-500/20 text-purple-400',
};

export default function ConventionManager() {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('events');
  const navigate = useNavigate();

  // Comparison states
  const [selectedEventsForCompare, setSelectedEventsForCompare] = useState([]);
  const [selectedYear1, setSelectedYear1] = useState(null);
  const [selectedYear2, setSelectedYear2] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, filterStatus]);

  // Initialize years when events load
  useEffect(() => {
    if (events.length > 0 && !selectedYear1) {
      const years = [...new Set(events.map(e => parseInt(e.start_date.split('-')[0])))].sort((a, b) => b - a);
      if (years.length >= 2) {
        setSelectedYear1(years[0]);
        setSelectedYear2(years[1]);
      } else if (years.length === 1) {
        setSelectedYear1(years[0]);
        setSelectedYear2(years[0]);
      }
    }
  }, [events, selectedYear1]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = events;
    if (searchTerm) {
      filtered = filtered.filter(
        (e) =>
          e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter((e) => e.application_status === filterStatus);
    }
    setFilteredEvents(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getProfitLoss = (event) => {
    return (event.total_revenue || 0) - (event.total_expenses || 0) - (event.booth_cost || 0);
  };

  const getEventYear = (event) => {
    return parseInt(event.start_date.split('-')[0]);
  };

  const groupedByYear = filteredEvents.reduce((acc, event) => {
    const year = getEventYear(event);
    if (!acc[year]) acc[year] = [];
    acc[year].push(event);
    return acc;
  }, {});

  const sortedYears = Object.keys(groupedByYear).sort((a, b) => b - a);
  const availableYears = [...new Set(events.map(e => getEventYear(e)))].sort((a, b) => b - a);

  // Toggle event selection for multi-show comparison
  const toggleEventSelection = (eventId) => {
    setSelectedEventsForCompare(prev => 
      prev.includes(eventId) 
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  // Get events for year-over-year comparison
  const eventsYear1 = selectedYear1 ? events.filter(e => getEventYear(e) === selectedYear1) : [];
  const eventsYear2 = selectedYear2 ? events.filter(e => getEventYear(e) === selectedYear2) : [];

  const calculateYearTotals = (yearEvents) => {
    return yearEvents.reduce((acc, event) => ({
      revenue: acc.revenue + (event.total_revenue || 0),
      expenses: acc.expenses + (event.total_expenses || 0),
      boothCosts: acc.boothCosts + (event.booth_cost || 0),
      profit: acc.profit + getProfitLoss(event),
      eventCount: acc.eventCount + 1
    }), { revenue: 0, expenses: 0, boothCosts: 0, profit: 0, eventCount: 0 });
  };

  const year1Totals = calculateYearTotals(eventsYear1);
  const year2Totals = calculateYearTotals(eventsYear2);

  // Find matching events between years
  const findMatchingEvents = () => {
    const matches = [];
    eventsYear1.forEach(e1 => {
      const match = eventsYear2.find(e2 => 
        e2.name.toLowerCase().replace(/\d{4}/g, '').trim() === 
        e1.name.toLowerCase().replace(/\d{4}/g, '').trim()
      );
      if (match) {
        matches.push({ year1: e1, year2: match });
      }
    });
    return matches;
  };

  const matchingEvents = findMatchingEvents();

  const getPercentChange = (current, previous) => {
    if (previous === 0) return current > 0 ? '+100%' : '0%';
    const change = ((current - previous) / Math.abs(previous)) * 100;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight" data-testid="convention-manager-title">
            Convention Manager
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Manage events and compare performance</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          data-testid="create-event-button"
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(34,211,238,0.3)]"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Event
        </Button>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-3 sm:w-auto sm:inline-flex">
          <TabsTrigger value="events" className="flex items-center gap-2 text-xs sm:text-sm">
            <Calendar className="h-4 w-4" /> Events
          </TabsTrigger>
          <TabsTrigger value="compare" className="flex items-center gap-2 text-xs sm:text-sm">
            <GitCompare className="h-4 w-4" /> Multi-Show
          </TabsTrigger>
          <TabsTrigger value="yoy" className="flex items-center gap-2 text-xs sm:text-sm">
            <TrendingUp className="h-4 w-4" /> Year-over-Year
          </TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="mt-4 space-y-4">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-secondary/50 border-transparent focus:border-primary"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
            >
              <option value="all">All Statuses</option>
              <option value="not_applied">Not Applied</option>
              <option value="applied">Applied</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="sold_out">Sold Out</option>
              <option value="attending_partner">With Partner</option>
            </select>
          </div>

          {/* Events List */}
          {filteredEvents.length === 0 ? (
            <Card className="p-12 text-center bg-card border-border/50">
              <p className="text-muted-foreground">No events found</p>
            </Card>
          ) : (
            <div className="space-y-6">
              {sortedYears.map((year) => (
                <div key={year}>
                  <div className="flex items-center gap-4 mb-3">
                    <h2 className="text-xl font-bold text-primary">{year}</h2>
                    <div className="flex-1 h-px bg-border"></div>
                    <span className="text-sm text-muted-foreground">{groupedByYear[year].length} events</span>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {groupedByYear[year].map((event) => {
                      const profitLoss = getProfitLoss(event);
                      return (
                        <Card
                          key={event.id}
                          onClick={() => navigate(`/events/${event.id}`)}
                          className="p-4 hover:border-primary/50 transition-colors cursor-pointer bg-card border-border/50"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="font-semibold truncate">{event.name}</h3>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[event.application_status]}`}>
                                  {event.application_status === 'attending_partner' 
                                    ? `Partner: ${event.partner_name || 'TBD'}`
                                    : event.application_status.replace('_', ' ').toUpperCase()}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground truncate">{event.location}</p>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm">
                              <div>
                                <p className="text-xs text-muted-foreground">Date</p>
                                <p className="font-mono">{formatDate(event.start_date)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Revenue</p>
                                <p className="font-mono text-green-400">{formatCurrency(event.total_revenue)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Profit</p>
                                <p className={`font-mono ${profitLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {formatCurrency(profitLoss)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Multi-Show Comparison Tab */}
        <TabsContent value="compare" className="mt-4 space-y-4">
          <Card className="bg-card border-border/50 p-4">
            <p className="text-sm text-muted-foreground mb-4">Select events to compare side-by-side</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
              {events.map((event) => (
                <label
                  key={event.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedEventsForCompare.includes(event.id) ? 'bg-primary/20 border border-primary' : 'bg-secondary/30 hover:bg-secondary/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedEventsForCompare.includes(event.id)}
                    onChange={() => toggleEventSelection(event.id)}
                    className="h-4 w-4 rounded border-border bg-secondary text-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm">{event.name}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(event.start_date)}</p>
                  </div>
                </label>
              ))}
            </div>
          </Card>

          {selectedEventsForCompare.length >= 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Comparison Results</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 font-medium">Event</th>
                      <th className="text-right p-3 font-medium">Revenue</th>
                      <th className="text-right p-3 font-medium">Booth Cost</th>
                      <th className="text-right p-3 font-medium">Expenses</th>
                      <th className="text-right p-3 font-medium">Profit</th>
                      <th className="text-right p-3 font-medium">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEventsForCompare.map((eventId) => {
                      const event = events.find(e => e.id === eventId);
                      if (!event) return null;
                      const profit = getProfitLoss(event);
                      const totalInvestment = (event.booth_cost || 0) + (event.total_expenses || 0);
                      const roi = totalInvestment > 0 ? (profit / totalInvestment * 100).toFixed(1) : 0;
                      return (
                        <tr key={eventId} className="border-b border-border/50 hover:bg-secondary/20">
                          <td className="p-3">
                            <p className="font-medium">{event.name}</p>
                            <p className="text-xs text-muted-foreground">{formatDate(event.start_date)}</p>
                          </td>
                          <td className="text-right p-3 font-mono text-green-400">{formatCurrency(event.total_revenue)}</td>
                          <td className="text-right p-3 font-mono text-orange-400">{formatCurrency(event.booth_cost)}</td>
                          <td className="text-right p-3 font-mono text-red-400">{formatCurrency(event.total_expenses)}</td>
                          <td className={`text-right p-3 font-mono ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {formatCurrency(profit)}
                          </td>
                          <td className={`text-right p-3 font-mono ${roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {roi}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {selectedEventsForCompare.length < 2 && (
            <Card className="p-8 text-center bg-card border-border/50">
              <GitCompare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Select at least 2 events to compare</p>
            </Card>
          )}
        </TabsContent>

        {/* Year-over-Year Tab */}
        <TabsContent value="yoy" className="mt-4 space-y-4">
          {/* Year Selectors */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Compare</label>
              <select
                value={selectedYear1 || ''}
                onChange={(e) => setSelectedYear1(parseInt(e.target.value))}
                className="px-3 py-2 rounded-lg bg-secondary/50 border-0 text-foreground"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <span className="text-muted-foreground">vs</span>
            <select
              value={selectedYear2 || ''}
              onChange={(e) => setSelectedYear2(parseInt(e.target.value))}
              className="px-3 py-2 rounded-lg bg-secondary/50 border-0 text-foreground"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Year Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-card border-primary/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-primary">{selectedYear1} Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Events</span><span className="font-bold">{year1Totals.eventCount}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><span className="font-bold text-green-400">{formatCurrency(year1Totals.revenue)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Expenses</span><span className="font-bold text-red-400">{formatCurrency(year1Totals.expenses + year1Totals.boothCosts)}</span></div>
                <div className="flex justify-between pt-2 border-t border-border"><span className="text-muted-foreground">Net Profit</span><span className={`font-bold ${year1Totals.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(year1Totals.profit)}</span></div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border/50">
              <CardHeader className="pb-2">
                <CardTitle>{selectedYear2} Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Events</span><span className="font-bold">{year2Totals.eventCount}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><span className="font-bold text-green-400">{formatCurrency(year2Totals.revenue)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Expenses</span><span className="font-bold text-red-400">{formatCurrency(year2Totals.expenses + year2Totals.boothCosts)}</span></div>
                <div className="flex justify-between pt-2 border-t border-border"><span className="text-muted-foreground">Net Profit</span><span className={`font-bold ${year2Totals.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(year2Totals.profit)}</span></div>
              </CardContent>
            </Card>
          </div>

          {/* Year-over-Year Changes */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-base">Year-over-Year Changes ({selectedYear2} → {selectedYear1})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-secondary/30 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Events</p>
                  <p className="text-lg font-bold">{year1Totals.eventCount - year2Totals.eventCount >= 0 ? '+' : ''}{year1Totals.eventCount - year2Totals.eventCount}</p>
                </div>
                <div className="bg-secondary/30 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Revenue</p>
                  <p className={`text-lg font-bold ${year1Totals.revenue >= year2Totals.revenue ? 'text-green-400' : 'text-red-400'}`}>
                    {getPercentChange(year1Totals.revenue, year2Totals.revenue)}
                  </p>
                </div>
                <div className="bg-secondary/30 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Expenses</p>
                  <p className={`text-lg font-bold ${year1Totals.expenses <= year2Totals.expenses ? 'text-green-400' : 'text-red-400'}`}>
                    {getPercentChange(year1Totals.expenses, year2Totals.expenses)}
                  </p>
                </div>
                <div className="bg-secondary/30 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground mb-1">Profit</p>
                  <p className={`text-lg font-bold ${year1Totals.profit >= year2Totals.profit ? 'text-green-400' : 'text-red-400'}`}>
                    {getPercentChange(year1Totals.profit, year2Totals.profit)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Same Show Comparison */}
          {matchingEvents.length > 0 && (
            <Card className="bg-card border-border/50">
              <CardHeader>
                <CardTitle className="text-base">Same Show Comparison</CardTitle>
                <p className="text-sm text-muted-foreground">Events appearing in both years</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {matchingEvents.map(({ year1: e1, year2: e2 }, idx) => {
                    const profit1 = getProfitLoss(e1);
                    const profit2 = getProfitLoss(e2);
                    const profitChange = profit1 - profit2;
                    return (
                      <div key={idx} className="p-3 bg-secondary/20 rounded-lg">
                        <h4 className="font-semibold mb-2">{e1.name.replace(/\d{4}/g, '').trim()}</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Year</p>
                            <p>{selectedYear1} vs {selectedYear2}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Revenue</p>
                            <p><span className="text-green-400">{formatCurrency(e1.total_revenue)}</span> <span className="text-muted-foreground">vs</span> {formatCurrency(e2.total_revenue)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Booth</p>
                            <p><span className="text-orange-400">{formatCurrency(e1.booth_cost)}</span> <span className="text-muted-foreground">vs</span> {formatCurrency(e2.booth_cost)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Profit</p>
                            <p><span className={profit1 >= 0 ? 'text-green-400' : 'text-red-400'}>{formatCurrency(profit1)}</span> <span className="text-muted-foreground">vs</span> {formatCurrency(profit2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Change</p>
                            <p className={profitChange >= 0 ? 'text-green-400' : 'text-red-400'}>{profitChange >= 0 ? '+' : ''}{formatCurrency(profitChange)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <EventDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          setDialogOpen(false);
          fetchEvents();
        }}
      />
    </div>
  );
}
